import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";
import ApiManager from "../../ApiManager/ApiManager";
import "./MapView.css";

// MapView now supports draggable markers, onMarkerDragEnd callback, and directions
const MapView = ({ markers = [], zoom = 13, height = 300, onMarkerDragEnd, showDirections = false, usePublicApi = false }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRefs = useRef([]);
  const polylineRef = useRef(null);
  const tileLayerRef = useRef(null);
  
  // State for directions functionality
  const [isLoadingDirections, setIsLoadingDirections] = useState(false);
  const [directionsError, setDirectionsError] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);
  
  // State for map style switching
  const [currentMapStyle, setCurrentMapStyle] = useState('roadmap');

  // Default coordinates (Delhi, India)
  const defaultLat = 28.5561;
  const defaultLng = 77.1;

  // Map style configurations
  const mapStyles = {
    roadmap: {
      url: "https://mt1.google.com/vt/lyrs=r&x={x}&y={y}&z={z}",
      attribution: '&copy; Google Maps',
      name: 'Roadmap'
    },
    satellite: {
      url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      attribution: '&copy; Google Maps',
      name: 'Satellite'
    },
    hybrid: {
      url: "https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}",
      attribution: '&copy; Google Maps',
      name: 'Hybrid'
    },
    terrain: {
      url: "https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}",
      attribution: '&copy; Google Maps',
      name: 'Terrain'
    }
  };

  // Helper: get valid marker positions
  const validMarkers = (markers || []).filter(
    m => typeof m.latitude === "number" && !isNaN(m.latitude) && typeof m.longitude === "number" && !isNaN(m.longitude)
  );

  // Helper: check if we can show directions (exactly 2 markers with reporting and drop)
  const canShowDirections = showDirections && validMarkers.length === 2 && 
    validMarkers.some(m => m.id === "reporting") && 
    validMarkers.some(m => m.id === "drop");

  // Helper: get reporting and drop markers
  const getReportingAndDropMarkers = () => {
    const reporting = validMarkers.find(m => m.id === "reporting");
    const drop = validMarkers.find(m => m.id === "drop");
    return { reporting, drop };
  };

  // Clear existing polyline
  const clearDirections = () => {
    if (polylineRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(polylineRef.current);
      polylineRef.current = null;
    }
    setRouteInfo(null);
    setDirectionsError(null);
  };

  // Decode Google's encoded polyline format
  const decodePolyline = (encoded) => {
    const points = [];
    let index = 0;
    const len = encoded.length;
    let lat = 0;
    let lng = 0;

    while (index < len) {
      let b;
      let shift = 0;
      let result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const dlat = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
      lat += dlat;

      shift = 0;
      result = 0;
      do {
        b = encoded.charCodeAt(index++) - 63;
        result |= (b & 0x1f) << shift;
        shift += 5;
      } while (b >= 0x20);
      const dlng = ((result & 1) !== 0 ? ~(result >> 1) : (result >> 1));
      lng += dlng;

      points.push([lat / 1e5, lng / 1e5]);
    }
    return points;
  };

  // Format distance and duration
  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Function to change map style
  const changeMapStyle = (styleKey) => {
    if (!mapInstanceRef.current || !mapStyles[styleKey]) return;
    
    // Remove current tile layer
    if (tileLayerRef.current) {
      mapInstanceRef.current.removeLayer(tileLayerRef.current);
    }
    
    // Add new tile layer
    const style = mapStyles[styleKey];
    tileLayerRef.current = L.tileLayer(style.url, {
      attribution: style.attribution,
      maxZoom: 20,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
    }).addTo(mapInstanceRef.current);
    
    setCurrentMapStyle(styleKey);
  };

  // Get directions from API
  const getDirections = async () => {
    if (!canShowDirections) return;

    const { reporting, drop } = getReportingAndDropMarkers();
    if (!reporting || !drop) return;

    setIsLoadingDirections(true);
    setDirectionsError(null);

    try {
      const params = new URLSearchParams({
        origin: `${reporting.latitude},${reporting.longitude}`,
        destination: `${drop.latitude},${drop.longitude}`,
        mode: 'driving'
      });

      // Use different API based on the usePublicApi prop
      const response = usePublicApi 
        ? await ApiManager.getDirectionBetweenTwoPointsPublic(params)
        : await ApiManager.getDirectionBetweenTwoPoints(params);
      
      if (!response.ok) {
        throw new Error('Failed to get directions');
      }

      const data = await response.json();
      
      // Clear existing polyline first
      clearDirections();

      // Handle the API response format
      if (data.success && data.data && data.data.routes && data.data.routes.length > 0) {
        const route = data.data.routes[0];
        
        // Decode the overview polyline
        if (route.overview_polyline) {
          const coordinates = decodePolyline(route.overview_polyline);
          
          polylineRef.current = L.polyline(coordinates, {
            color: '#4285F4',
            weight: 5,
            opacity: 0.9
          }).addTo(mapInstanceRef.current);

          // Fit map to show the entire route
          const bounds = L.latLngBounds([
            [reporting.latitude, reporting.longitude],
            [drop.latitude, drop.longitude],
            ...coordinates
          ]);
          mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20] });

          // Store route info from legs
          if (route.legs && route.legs.length > 0) {
            const leg = route.legs[0];
            setRouteInfo({
              distance: formatDistance(leg.distance),
              duration: formatDuration(leg.duration)
            });
          }
        } else {
          throw new Error('No polyline data in response');
        }
      } else {
        throw new Error('Invalid response format');
      }

    } catch (error) {
      console.error('Error getting directions:', error);
      setDirectionsError('Failed to get directions. Please try again.');
    } finally {
      setIsLoadingDirections(false);
    }
  };

    // Initialize map only once
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create the map instance
    mapInstanceRef.current = L.map(mapRef.current).setView([defaultLat, defaultLng], zoom);
      
    // Add initial tile layer (roadmap)
    const initialStyle = mapStyles[currentMapStyle];
    tileLayerRef.current = L.tileLayer(initialStyle.url, {
      attribution: initialStyle.attribution,
      maxZoom: 20,
      subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
    }).addTo(mapInstanceRef.current);

    // Ensure map is properly sized
    mapInstanceRef.current.invalidateSize();

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        markerRefs.current = [];
      }
    };
  }, []);

  // Update markers when they change
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    // Clear existing directions when markers change
    clearDirections();

    // Remove existing markers
    markerRefs.current.forEach(marker => {
      mapInstanceRef.current.removeLayer(marker);
    });
    markerRefs.current = [];

    // Create custom red icon
    const icon = L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
      shadowUrl: markerShadowPng,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41]
    });

    // Add new markers
    markerRefs.current = validMarkers.map((m, idx) => {
      const marker = L.marker([m.latitude, m.longitude], {
        icon,
        draggable: !!m.draggable
      }).addTo(mapInstanceRef.current);
      
      if (m.label) marker.bindPopup(m.label);
      
      // Add dragend event if draggable and callback provided
      if (m.draggable && typeof onMarkerDragEnd === 'function' && m.id) {
        marker.on('dragend', (e) => {
          const latlng = e.target.getLatLng();
          onMarkerDragEnd(m.id, { latitude: latlng.lat, longitude: latlng.lng });
        });
      }
      return marker;
    });

    // Update map view
    if (validMarkers.length > 0) {
      // Center: if at least one marker, use first
      const center = [validMarkers[0].latitude, validMarkers[0].longitude];
      
      // Fit bounds if more than one marker (but only if no polyline is showing)
      if (validMarkers.length > 1 && !polylineRef.current) {
        const bounds = L.latLngBounds(validMarkers.map(m => [m.latitude, m.longitude]));
        mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40] });
      } else if (!polylineRef.current) {
        mapInstanceRef.current.setView(center, zoom);
      }
    }
  }, [JSON.stringify(validMarkers), onMarkerDragEnd]);

  // Handle zoom changes separately
  useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setZoom(zoom);
  }, [zoom]);

  return (
    <div className="map-container" style={{ height, width: "100%" }}>
      <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
      
      {/* Map Style Switcher */}
      <div className="map-style-switcher">
        <select 
          value={currentMapStyle} 
          onChange={(e) => changeMapStyle(e.target.value)}
          className="style-selector"
        >
          {Object.entries(mapStyles).map(([key, style]) => (
            <option key={key} value={key}>
              {style.name}
            </option>
          ))}
        </select>
      </div>
      
      {/* Directions Controls */}
      {canShowDirections && (
        <div className="directions-controls">
          {!polylineRef.current ? (
            <button
              className="directions-button"
              onClick={getDirections}
              disabled={isLoadingDirections}
            >
              {isLoadingDirections ? "Getting Directions..." : "🗺️ Get Directions"}
            </button>
          ) : (
            <div className="directions-info">
              <button
                className="directions-button clear"
                onClick={clearDirections}
              >
                Clear Route
              </button>
              {routeInfo && (
                <div className="route-info">
                  {routeInfo.distance && <span>📏 {routeInfo.distance}</span>}
                  {routeInfo.duration && <span>⏱️ {routeInfo.duration}</span>}
                </div>
              )}
            </div>
          )}
          
          {directionsError && (
            <div className="directions-error">
              ⚠️ {directionsError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapView;
